import { useEffect } from 'react';
import { atom, selectorFamily, useRecoilState } from 'recoil';

import { useLazyRef } from '@gitbook/util-react-hooks';

export enum InteractionLocks {
    KEYBOARD_ESCAPE = 'KEYBOARD_ESCAPE',
    KEYBOARD_TAB = 'KEYBOARD_TAB',
    OUTSIDE_CLICK = 'OUTSIDE_CLICK',
}

let ID = 0;

const locksAtoms = atom<{
    [key in InteractionLocks]: string[];
}>({
    key: 'interaction-locks',
    default: {
        [InteractionLocks.KEYBOARD_ESCAPE]: [],
        [InteractionLocks.KEYBOARD_TAB]: [],
        [InteractionLocks.OUTSIDE_CLICK]: [],
    },
});

const lockAtom = selectorFamily<string[], InteractionLocks>({
    key: 'interaction-lock',
    get:
        (lock) =>
        ({ get }) => {
            return get(locksAtoms)[lock];
        },

    set:
        (lock) =>
        ({ set }, newValue) => {
            set(locksAtoms, (prev) => ({
                ...prev,
                [lock]: newValue,
            }));
        },
});

/**
 * Returns whether the component calling the hook is the first responder for
 * a specific interaction (for example "responding to ESCAPE").
 *
 * Several components might listen to a specific interaction but only one should
 * respond to an individual event (to avoid closing all the modals, popover
 * and sidesheets as once for example). Only the top-most should.
 * That component is called the "first responder".
 *
 * So when implementing a component that listen to a specific interaction, you should
 * check if it's the first responder:
 *
 * ```js
 * function MyModal({ opened, onClose }) {
 *     const isFirstResponder = useFirstResponderInteraction(InteractionLocks.OUTSIDE_CLICK, opened);
 *
 *     if (!opened) { return null; }
 *     return (
 *         // backdrop
 *         <TouchableOpacity onPress={() => {
 *             if (isFirstResponder) { onClose(); }
 *         }}>
 *             <ModalBody>...</ModalBody>
 *         </TouchableOpacity>
 *     );
 * }
 * ```
 */
export function useFirstResponderInteraction(
    lockType: InteractionLocks,
    isResponder = true,
    deps: any[] = []
): boolean {
    const idRef = useLazyRef(() => {
        ID += 1;
        return `${ID}`;
    });
    const [lock, setLock] = useRecoilState(lockAtom(lockType));

    useEffect(() => {
        if (!isResponder) {
            return;
        }
        setLock((prev) => {
            return [idRef.current, ...prev];
        });

        return () => {
            setLock((prev) => {
                return prev.filter((id) => id !== idRef.current);
            });
        };
    }, [...deps, isResponder, idRef, lockType, setLock]);

    return lock[0] === idRef.current;
}
